import { AfterViewInit, Component, ErrorHandler, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy, Injector, inject, signal } from '@angular/core'
import { form } from '@angular/forms/signals'
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog'
import { MatPaginator } from '@angular/material/paginator'
import { ActivatedRoute, Router } from '@angular/router'

import { LaskuKatseleComponentDataResolve, LaskuKatseleComponentExistingData, LaskutComponentData } from '../_angular/_resolvers/lasku.resolve'
import { KayttajaService } from '../_angular/service/kayttaja.service'
import { LaskuSummatService } from '../_angular/service/lasku/lasku-summat.service'
import { LaskuService } from '../_angular/service/lasku/lasku.service'
import { LaskutusHakuStateService } from '../_jaettu-angular/laskut/haku/laskutus-haku-state.service'
import { VersionTarkistusPalvelu } from '../_angular/service/version-tarkistus.palvelu'
import { laskeYhteissummatValuutoittain, YhteensaRivi } from './laskulista-logiikka'
import { LaskunLokalisoituKuukausi, LaskunLokalisoituTila, LaskutFirestoreDataSource } from './laskut.firestore.datasource'

import { LoadingConfig } from '../_jaettu-angular/_components/loading.component'
import { arvonMuutokset, asetaArvo } from '../_jaettu-angular/_lomakkeet/arvon-muutokset'
import { WindowSizeService } from '../_jaettu-angular/service/window.service'

import { Lasku, LaskunAsiakas, LaskunListaustietorivi, LaskunTila } from '@lemon/core-firebase/model/lasku'

import {
  LaskuHaluatkoVarmastiPeruaMaksetuksiMerkitsemisenDialog,
  LaskuHaluatkoVarmastiPeruaMaksetuksiMerkitsemisenDialogData,
} from './dialogit/lasku.haluatko-varmasti-perua-maksetuksi-merkitsemisen.dialog'

import { BankCustomerConsent, BankDialogType, DialogToShowMetadata } from '@lemon/core-firebase/model/banks'
import { BankConsentUriService } from '@lemon/core-firebase/service/bank-consent-uri.service'
import { NumberMonth } from '@lemon/core/model/common'
import { DateService } from '@lemon/core/service/date.service'
import { CurrencyService } from '@lemon/core/service/currency.service'
import { LaskuSharedService } from '@lemon/core-firebase/service/lasku/lasku-shared.service'
import { AsetustenOletuksetService } from 'app/_angular/service/asetusten-oletukset.service'
import { EnableBankingDialogService } from 'app/_angular/service/enable-banking-dialog.service'
import { FirebaseLemonaid } from 'app/_angular/service/firebase-lemonaid.service'
import { lemonShare } from 'app/_jaettu-angular/_rxjs/lemon-share.operator'
import { LadataanService } from 'app/_jaettu-angular/service/ladataan.service'
import { HolviLoginRequiredDialog, HolviLoginRequiredDialogData } from 'app/banks/holvi/holvi-login-required.dialog'
import { AiiaDialogData, ViiaLoginRequiredDialog } from 'app/banks/viia/viia-login-required.dialog'
import { combineLatest, EMPTY, firstValueFrom, iif, Observable, of as observableOf, Subject } from 'rxjs'
import { distinctUntilChanged, map, startWith, switchMap, take, takeUntil, throttleTime } from 'rxjs/operators'
import { EnableBankingDialogData, EnableBankingLoginRequiredDialog } from '../banks/enable-banking/enable-banking-login-required.dialog'
import { GeneralBankSelectionDialog, GeneralBankSelectionDialogData } from '../banks/general-bank-selection.dialog'
import { LaskuKommenttiDialog, LaskuKommenttiDialogData } from './dialogit/lasku-kommentti.dialog'
import { LaskuTaydennaAsetuksetDialog } from './dialogit/lasku-taydenna-asetukset.dialog'
import { LaskutusprofiiliService } from '../_angular/service/lasku/laskutusprofiili.service'

// export type TuoteProperties = 'nro' | 'asiakas' | 'pvm' | 'erapvm' | 'summa' | 'avoinna' | 'tila' | 'merkitse' | undefined

interface LaskuhakuLomake {
  tila: LaskunTila | null
  kuukausi: NumberMonth | null
}

@Component({
  selector: 'app-laskut',
  templateUrl: './laskut.component.html',
  styleUrls: ['./laskut.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class LaskutComponent implements OnInit, AfterViewInit, OnDestroy {
  // @ViewChild('laskutaulu', { static: true }) laskutaulu: ElementRef
  @ViewChild(MatPaginator, { static: true }) _paginator: MatPaginator

  private _ngUnsubscribe = new Subject<void>()
  private _taydennaAsetuksetDialogi: MatDialogRef<LaskuTaydennaAsetuksetDialog> = null
  private _kommenttiDialogi: MatDialogRef<LaskuKommenttiDialog> = null
  private readonly _injector = inject(Injector)

  // HUOM! Default arvot laitetaan hakutiedotObservable subscribessa
  lomake = form(signal<LaskuhakuLomake>({ tila: null, kuukausi: null }))
  hakuAvoinna = false

  // propertiesToDisplay: TuoteProperties[] = []
  laskunTilatObservable: Observable<LaskunLokalisoituTila[]>
  kuukausivaihtoehdotObservable: Observable<LaskunLokalisoituKuukausi[]>
  tableViewOptionsObservable: Observable<{ showAddButton: boolean; showCommentsColumn: boolean }>

  nakyvatRivitObservable: Observable<LaskunListaustietorivi[]>
  yhteensaRivitObservable: Observable<YhteensaRivi[]>

  private _bankConsentObservable: Observable<BankCustomerConsent>
  showBankAuthNeededObservable: Observable<boolean>
  onkoPankkiyhteysrooliObservable: Observable<boolean>

  naytaEiYhtaanObservable: Observable<boolean>
  lataaObservable: Observable<boolean>
  private _buttonPushInflight: boolean = false

  countToAvoinna: number = 0
  countToEraantynyt: number = 0
  readonly avoinnaTo = signal(0)
  readonly avoinnaFrom = signal(0)
  readonly eraantynytTo = signal(0)
  readonly eraantynytFrom = signal(0)
  readonly valuutta = signal('')

  private _alustaSummat = true
  private _pauser = new Subject<boolean>()

  loadingSettings: LoadingConfig = {
    fullScreenBackdrop: false,
    backdropBackgroundColour: 'rgba(0,0,0,0.15)',
    backdropBorderRadius: '4px',
    primaryColour: 'rgba(255, 255, 255, 1)',
    secondaryColour: 'rgba(255, 255, 255, 1)', // rgba(252, 238, 32, .15)
    tertiaryColour: 'rgba(255, 255, 255, 1)',
  }

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private errorHandler: ErrorHandler,
    private _dialog: MatDialog,
    private laskuService: LaskuService,
    private kayttajaService: KayttajaService,
    private laskuSummatService: LaskuSummatService,
    private laskuKatseleComponentDataResolve: LaskuKatseleComponentDataResolve,
    private windowSizeService: WindowSizeService,
    private _dataSource: LaskutFirestoreDataSource,
    private versionTarkistaja: VersionTarkistusPalvelu,
    private asetustenOletuksetService: AsetustenOletuksetService,
    private _firebaseLemonaid: FirebaseLemonaid,
    private _bankConsentUriService: BankConsentUriService,
    private _ladataanService: LadataanService,
    private _enableBankingDialogService: EnableBankingDialogService,
    private _hakuStateService: LaskutusHakuStateService,
    private _dateService: DateService,
    private _laskuSharedService: LaskuSharedService,
    private _currencyService: CurrencyService,
    private _laskutusprofiiliService: LaskutusprofiiliService,
  ) {
    this.lataaObservable = _dataSource.lataaObservable
    this.naytaEiYhtaanObservable = combineLatest([this._dataSource.rivienMaaraObservable, this._dataSource.lataaObservable]).pipe(
      map(([rivienMaara, lataa]) => {
        if (lataa) {
          return false
        }
        return rivienMaara < 1
      }),
    )
  }

  ngOnInit() {
    if (this._hakuStateService.lasku.onkoHakutilaTallennettu(this.router.url)) {
      this.hakuAvoinna = true
    }

    this.versionTarkistaja.sovelluksenVersioObservable.pipe(takeUntil(this._ngUnsubscribe)).subscribe(versiotiedot => {
      this.versionTarkistaja.tarkistaVersio(versiotiedot)
    })

    this.asetaListauksenKolumnit()

    // this.laskuService.asetuksetObservable.pipe(
    //   takeUntil(this.ngUnsubscribe)
    // ).subscribe(asetukset => {
    //   if (asetukset && !asetukset.summahuomautusPiilotettu) {
    //     this.naytaHuomautus = true
    //   } else {
    //     this.naytaHuomautus = false
    //   }
    // }, error => {
    //   this.errorHandler.handleError(error)
    // })

    this._pauser
      .pipe(
        switchMap(aktiivinen => {
          return iif(() => aktiivinen, this.laskuSummatService.summatObservable, EMPTY)
        }),
        throttleTime(1100, undefined, { leading: true, trailing: true }),
        takeUntil(this._ngUnsubscribe),
      )
      .subscribe(
        summat => {
          // console.log('summat', JSON.stringify(summat))
          if (summat) {
            // Mikä valuutta
            this.valuutta.set(summat.valuutta)

            // Mistä rullataan
            this.avoinnaFrom.set(summat.avoinnaAlkuperainen)
            this.eraantynytFrom.set(summat.eraantynytAlkuperainen)

            // Mihin rullataan
            if (this._alustaSummat) {
              this._alustaSummat = false
              setTimeout(() => {
                this.avoinnaTo.set(summat.avoinna)
                this.eraantynytTo.set(summat.eraantynyt)
              }, 150)
            } else {
              this.avoinnaTo.set(summat.avoinna)
              this.eraantynytTo.set(summat.eraantynyt)
            }
          }
        },
        error => {
          this.errorHandler.handleError(error)
        },
      )
    this._pauser.next(true)

    this.laskunTilatObservable = this.dataSource.laskunTilatObservable
    this.kuukausivaihtoehdotObservable = this.dataSource.kuukausivaihtoehdotObservable
    this.tableViewOptionsObservable = this.kayttajaService.kayttajanTiedotObservable.pipe(
      map(kayttaja => {
        return { showAddButton: kayttaja?.asiakasId !== '9', showCommentsColumn: kayttaja?.asiakasId === '9' }
      }),
      startWith({ showAddButton: true, showCommentsColumn: false }),
    )

    this._bankConsentObservable = this.kayttajaService.kayttajanTiedotObservable.pipe(
      distinctUntilChanged((prev, curr) => prev?.asiakasAvain === curr?.asiakasAvain),
      switchMap(kayttajanTiedot => {
        if (!kayttajanTiedot?.asiakasAvain) {
          return observableOf<BankCustomerConsent>(null)
        }
        return this._firebaseLemonaid.firestoreDoc<BankCustomerConsent>(this._bankConsentUriService.getBankConsentUri(kayttajanTiedot.asiakasAvain)).listen()
      }),
    )

    this.showBankAuthNeededObservable = combineLatest([this.laskuService.asetuksetObservable, this._laskutusprofiiliService.laskutusprofiilienIbanitObservable]).pipe(
      switchMap(([laskuasetukset, laskutusprofiilienIbanit]) => {
        if (!laskuasetukset?.maksupReskontraActive) {
          return observableOf<boolean>(false)
        }
        return this._bankConsentObservable.pipe(
          map(bankConsent => {
            if (!bankConsent || bankConsent.dialogCompletePreventReShow?.length) {
              return false
            }
            return bankConsent.dialogsToShow?.some(d => this._puuttuukoLaskutusprofiilinTililtaPankkiyhteys(d.ibansMissing, laskutusprofiilienIbanit))
          }),
        )
      }),
    )

    this.onkoPankkiyhteysrooliObservable = this.kayttajaService.kayttajaObservable.pipe(map(kayttaja => !!kayttaja?.roolit?.HALLINTO_VIIA_VALTUUTUKSEN_ANTAJA))

    // Jos hakutiedot muuttuvat, muuta käyttöliittymää sen mukaan
    this.dataSource.hakutiedotObservable.pipe(takeUntil(this._ngUnsubscribe)).subscribe(hakutiedot => {
      if (this.lomake.tila().value() !== hakutiedot.hakukriteerit.tila) {
        asetaArvo(this.lomake.tila, hakutiedot.hakukriteerit.tila)
      }
      if (this.lomake.kuukausi().value() !== hakutiedot.hakukriteerit.kuukausi) {
        asetaArvo(this.lomake.kuukausi, hakutiedot.hakukriteerit.kuukausi)
      }
    })

    arvonMuutokset(this.lomake.tila, { injector: this._injector }).subscribe(value => {
      this.dataSource.tila = value
    })

    arvonMuutokset(this.lomake.kuukausi, { injector: this._injector }).subscribe((value: NumberMonth | null) => {
      this.dataSource.kuukausi = value
    })

    this._paginator.page.subscribe(
      page => {
        this.dataSource.page = page
      },
      error => {
        this.errorHandler.handleError(error)
      },
    )

    this.route.data.pipe(takeUntil(this._ngUnsubscribe)).subscribe(
      (data: { data: LaskutComponentData }) => {
        if (data && data.data && data.data.nollaaHakuasetukset) {
          this.dataSource.resetSearchToDefaults()
        }
      },
      error => {
        this.errorHandler.handleError(error)
      },
    )

    this.nakyvatRivitObservable = this.dataSource.dataSourceTransformed.connect(null)
    this.yhteensaRivitObservable = this.nakyvatRivitObservable.pipe(
      map(laskut => laskeYhteissummatValuutoittain(laskut, rivi => this._annaRivinEurokurssi(rivi), this._currencyService)),
      lemonShare(),
    )
  }

  private _annaRivinEurokurssi(lasku: LaskunListaustietorivi): number | undefined {
    if (lasku.valuutta === 'EUR' || !lasku.juurilasku) {
      return undefined
    }
    return this._laskuSharedService.annaViimeisinTavallinenLasku(lasku.juurilasku)?.valuuttakonversio?.kurssi?.rate
  }

  get dataSource(): LaskutFirestoreDataSource {
    return this._dataSource
  }

  avaaHaku() {
    this.hakuAvoinna = true
  }

  suljeHaku() {
    this.hakuAvoinna = false
  }

  ngAfterViewInit() {
    this.windowSizeService.sizeObservable.pipe(takeUntil(this._ngUnsubscribe)).subscribe(koko => {
      setTimeout(() => {
        this.asetaListauksenKolumnit()
      }, 250)
    })
  }

  private asetaListauksenKolumnit() {
    // this.naytaNro = true
    // this.naytaPvm = true
    // this.naytaSumma = true
    // const leveys = this.laskutaulu.nativeElement.offsetWidth
    // // if (tablenKoko < 500) {
    // //   this.propertiesToDisplay = [               'asiakas',         'erapvm',         'avoinna', 'merkitse']
    // // } else
    // if (leveys < 600) {
    //   this.propertiesToDisplay = ['tila', 'nro', 'asiakas', 'erapvm', 'avoinna', 'merkitse']
    //   // this.naytaNro = false
    //   this.naytaPvm = false
    //   this.naytaSumma = false
    // } else if (leveys < 700) {
    //   this.propertiesToDisplay = ['tila', 'nro', 'asiakas', 'erapvm', 'summa', 'avoinna', 'merkitse']
    //   // this.naytaNro = false
    //   this.naytaPvm = false
    // } else if (leveys < 800) {
    //   this.propertiesToDisplay = ['tila', 'nro', 'asiakas', 'pvm', 'erapvm', 'summa', 'avoinna', 'merkitse']
    //   // this.naytaNro = false
    // } else {
    //   this.propertiesToDisplay = ['tila', 'nro', 'asiakas', 'pvm', 'erapvm', 'summa', 'avoinna', 'merkitse']
    // }
  }

  ngOnDestroy() {
    this._ngUnsubscribe.next()
    this._ngUnsubscribe.complete()
  }

  getDisplayFn() {
    return val => this.display(val)
  }

  private display(laskunAsiakas: LaskunAsiakas): string {
    if (laskunAsiakas && laskunAsiakas.nimi) {
      return laskunAsiakas.nimi
    }
    return ''
  }

  onCountToEnd() {
    this.laskuSummatService.numerotRullattiin()
  }

  annaPaiviaMyohassa(rivi: LaskunListaustietorivi) {
    return this._dateService.paiviaValissaPaikallinen(this._dateService.currentLocalDate(), rivi.erapvm)
  }

  muokkaa(rivi: LaskunListaustietorivi) {
    const existing: LaskuKatseleComponentExistingData = {
      juurilasku: rivi.juurilasku,
      kasiteltava: rivi.kasiteltava,
    }
    this.laskuKatseleComponentDataResolve.asetaOlemassaolevaData(existing)
    this.router.navigate(['/laskutus/laskut/', rivi.juuriAvain, rivi.avain, 'katsele'])
  }

  lisaa() {
    this.laskuService.asetuksetObservable.pipe(take(1)).subscribe(asetukset => {
      if (asetukset) {
        if (this.asetustenOletuksetService.tarkistaPuuttuukoLaskuasetuksia(asetukset)) {
          this.naytaTaydennaAsetuksetDialogi()
        } else {
          // Sulje dialogi, jos sellainen oli
          if (this._taydennaAsetuksetDialogi) {
            this._taydennaAsetuksetDialogi.close()
            this._taydennaAsetuksetDialogi = null
          }
          this.router.navigate(['/laskutus/laskut/', 'uusi', 'uusi'])
        }
      }
    })
  }

  private naytaTaydennaAsetuksetDialogi() {
    if (!this._taydennaAsetuksetDialogi) {
      const kaytettavaLeveys = window.innerWidth
      const kaytettavaKorkeus = window.innerHeight
      const leveys = kaytettavaLeveys > 900 ? '90vw' : '98vw'
      const korkeus = kaytettavaKorkeus > 1100 ? '80vh' : kaytettavaKorkeus > 750 ? '85vh' : '92vh'

      const settings: MatDialogConfig = {
        maxWidth: '900px',
        maxHeight: korkeus,
        width: leveys,
        data: {
          mobiili: kaytettavaLeveys < 670,
        },
      }

      this._taydennaAsetuksetDialogi = this._dialog.open(LaskuTaydennaAsetuksetDialog, settings)
      this._taydennaAsetuksetDialogi.afterClosed().subscribe(result => {
        this._taydennaAsetuksetDialogi = null
        if (result === 'onnistui') {
          this.router.navigate(['/laskutus/laskut/', 'uusi', 'uusi'])
        }
      })
    }
  }

  async merkitseMaksetuksi(rivi: LaskunListaustietorivi) {
    if (this._buttonPushInflight) {
      return
    }
    this._buttonPushInflight = true

    try {
      if (this.vertaaEkaPienempiKuinToka(0, rivi.avoinna)) {
        await this.laskuService.merkitseLaskuTaysinMaksetuksi(rivi)
        // TODO: Ei käytä palautunutta laskua rivin päivittämiseen
      } else {
        const data: LaskuHaluatkoVarmastiPeruaMaksetuksiMerkitsemisenDialogData = {
          rivi: rivi,
        }
        const settings: MatDialogConfig = {
          data: data,
          panelClass: 'ilman-paddingia',
          maxWidth: '1600px',
          // ,
          // maxHeight: korkeus,
          // width: leveys
        }
        this._pauser.next(false)
        this._dialog
          .open(LaskuHaluatkoVarmastiPeruaMaksetuksiMerkitsemisenDialog, settings)
          .afterClosed()
          .subscribe(() => {
            this._pauser.next(true)
          })
      }
    } catch (error) {
      this.errorHandler.handleError(error)
    } finally {
      this._buttonPushInflight = false
    }
  }

  vertaaEkaPienempiKuinToka(pienempi: number, suurempi: number): boolean {
    return this._currencyService.roundHalfUp(pienempi, 2) < this._currencyService.roundHalfUp(suurempi, 2)
  }

  modifyComment(lasku: Lasku) {
    const kaytettavaLeveys = window.innerWidth
    const kaytettavaKorkeus = window.innerHeight
    const leveys = kaytettavaLeveys > 900 ? '90vw' : '98vw'
    const korkeus = kaytettavaKorkeus > 1100 ? '80vh' : kaytettavaKorkeus > 750 ? '85vh' : '92vh'

    const settings: MatDialogConfig<LaskuKommenttiDialogData> = {
      maxWidth: '900px',
      maxHeight: korkeus,
      width: leveys,
      data: { lasku: lasku },
    }
    if (this._kommenttiDialogi) {
      return
    }
    this._kommenttiDialogi = this._dialog.open(LaskuKommenttiDialog, settings)
    this._kommenttiDialogi.afterClosed().subscribe(result => {
      this._kommenttiDialogi = null
      if (result === 'onnistui') {
        /* NOOP */
      }
    })
  }

  private _puuttuukoLaskutusprofiilinTililtaPankkiyhteys(pankkiyhteydettomatIbanit: string[] | undefined, laskutusprofiilienIbanit: string[]): boolean {
    return laskutusprofiilienIbanit.some(iban => pankkiyhteydettomatIbanit?.includes(iban))
  }

  async openBankConsentDialog() {
    try {
      this._ladataanService.aloitaLataaminen()
      const consentPromise = firstValueFrom(this._bankConsentObservable)
      const laskutusprofiilienIbanitPromise = firstValueFrom(this._laskutusprofiiliService.laskutusprofiilienIbanitObservable)

      const [consent, laskutusprofiilienIbanit] = await Promise.all([consentPromise, laskutusprofiilienIbanitPromise])
      const dialogMetadata = consent?.dialogsToShow?.find(metadata => this._puuttuukoLaskutusprofiilinTililtaPankkiyhteys(metadata.ibansMissing, laskutusprofiilienIbanit))
      if (!dialogMetadata) {
        return
      }
      if (dialogMetadata.dialogToShow === BankDialogType.GENERAL) {
        return this._openGeneralBankConnectionDialog()
      }
      if (dialogMetadata.dialogToShow === BankDialogType.ENABLE_BANKING) {
        return this._openEnableBankingDialog(dialogMetadata)
      }
      if (dialogMetadata.dialogToShow === BankDialogType.PSD2_RENEW) {
        return this._openAiiaDialog(dialogMetadata)
      }
      if (dialogMetadata.dialogToShow === BankDialogType.HOLVI_RENEW) {
        return this._openHolviDialog(dialogMetadata)
      }
      throw new Error('Unknown dialog type ' + dialogMetadata.dialogToShow)
    } catch (err) {
      this.errorHandler.handleError(new Error('Failed to open bank dialog: ' + err?.message))
    } finally {
      this._ladataanService.lopetaLataaminen()
    }
  }

  private _openAiiaDialog(dialogToShow: DialogToShowMetadata) {
    const settings: MatDialogConfig<AiiaDialogData> = {
      data: {
        dialogToShowMetadata: dialogToShow ?? null,
      },
    }
    return this._dialog.open<ViiaLoginRequiredDialog, AiiaDialogData>(ViiaLoginRequiredDialog, settings)
  }

  private _openGeneralBankConnectionDialog() {
    const settings: MatDialogConfig<GeneralBankSelectionDialogData> = {
      data: {},
    }
    return this._dialog.open<GeneralBankSelectionDialog, GeneralBankSelectionDialogData>(GeneralBankSelectionDialog, settings)
  }

  private async _openEnableBankingDialog(dialogToShow: DialogToShowMetadata) {
    // Esiladataan ASPSP-lista ennen dialogin avaamista, jotta picker-chipit renderöityvät synkronisesti.
    const bankSubtypes = await this._enableBankingDialogService.getAspspDisplayData()
    const settings: MatDialogConfig<EnableBankingDialogData> = {
      data: {
        dialogToShowMetadata: dialogToShow ?? null,
        bankSubtypes,
      },
    }
    return this._dialog.open<EnableBankingLoginRequiredDialog, EnableBankingDialogData>(EnableBankingLoginRequiredDialog, settings)
  }

  private _openHolviDialog(dialogToShow: DialogToShowMetadata) {
    const settings: MatDialogConfig<HolviLoginRequiredDialogData> = {
      data: {},
    }
    return this._dialog.open<HolviLoginRequiredDialog, HolviLoginRequiredDialogData>(HolviLoginRequiredDialog, settings)
  }
}
